import { useState, useEffect, useCallback } from "react";
import api from "api/api";
import { Link, useNavigate } from "react-router-dom";

//Props Validations
import PropTypes from "prop-types";

// MUI components
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";

//import component
import FiltersHeaderLabel from "../components/common/FiltersHeaderLabel";
import Header from "../components/common/Header";
import Loader from "../components/common/Loader";
import MainNavigation from "../components/common/MainNavigation";
import FilterAcrossProductsToggleComponent from "../components/common/FilterAcrossProductsToggleComponent";
import Reset from "../components/common/Reset";
import TTestDialog from "../components/common/TTest/TTestDialog";
import FullScreenConatiner from "../components/layouts/FullScreenConatiner";
import PageLayout from "../components/expressCLT/productPreferenceAnalysis/PageLayout";

// redux
import { useSelector, useDispatch } from "react-redux";
import { loadProductPreferenceAnalysisResultState, loadDefaultProductPreferenceAnalysisResultState, loadIsProductPreferenceAnalysisResultLoadingState, loadPercentageToggleState, loadSelectedMicroCategoryState } from "store/features/analysisResultsSlice";
import { loadProductPreferenceAnalysisTypeValueState } from "store/features/analysisTypeSelectionSlice";

import { resetTTestStore } from "store/features/ttestSlice";
import { loadPreferenceRemovedKpiListState } from "store/features/analysisKpiSlice";
import { showNotification } from "store/features/notificationSlice";

// utils
import GetProductPreferenceResult from "../utils/apis/GetProductPreferenceResult";
import SkewTabs from "../components/common/analysisModule/SkewTabs";
import AnalysisTableBottomLabel from "../components/common/analysisModule/AnalysisTableBottomLabel";
function PreferenceAnalysisExpressCLT({ headerTitle = "" }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);
    const isLoading = useSelector((state) => state.analysisResultsStore.isProductPreferenceAnalysisResultLoading);
    const productsSecondaryDataState = useSelector((state) => state.analysisResultsStore.productPreferenceAnalysisResult);
    const percentageToggle = useSelector((state) => state.analysisResultsStore.percentageToggle);

    const productsOrder = useSelector((state) => state.productsInfoStore.productsOrder);
    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const productCategoryId = productCategoryData?.value || null;

    // Analysis type
    const analysisTypeValue = useSelector((state) => state.analysisTypeSelectionStore.productPreferenceAnalysisTypeValue);

    const [dataMappingData, setDataMappingData] = useState([]);

    const [isDataMappingDataLoading, setIsDataMappingDataLoading] = useState(false);
    const selectedMicroCategory = useSelector((state) => state.analysisResultsStore.selectedMicroCategory);
    const activeTab = selectedMicroCategory;

    // t-test details
    const [tTestResult, setTTestResult] = useState([]);
    const [testLabel, setTestLabel] = useState("");
    const [isFisher, setIsFisher] = useState(false);
    const [legends, setLegends] = useState([]);
    const [productsDataForTTest, setProductsDataForTTest] = useState([]);

    // action standard details
    const [actionStandardKpis, setActionStandardKpis] = useState([]);
    const [actionStandardData, setActionStandardData] = useState([]);

    const btnArr = ["fragranceMetricsAnalysisExpressCLT", "fragranceAssociation", "agreementKpi", "preferenceAnalysis"];
    const [btnDisabled, setBtnDisabled] = useState({});

    useEffect(() => {
        let _selectedStudies = selectedStudy;

        let disableBtn = _selectedStudies.length === 0;
        let tempObj = {
            fragranceMetricsAnalysis: disableBtn,
            fragranceAssociation: disableBtn,
            agreementkpi: disableBtn,
            productPreferenceAnalysis: disableBtn,
        };
        setBtnDisabled(tempObj);
    }, [selectedStudy]);

    useEffect(() => {
        if (!productsSecondaryDataState.length) return;
        const filteredRow = productsSecondaryDataState.map((group) => {
            const compPart = group.scores.map((c) => `${c.title} / ${c.study_reference_title}`).join(" vs ");
            const productsArr = group.scores.map((c) => c.title);
            return {
                backendVariable: group.backendVariable,
                title: compPart,
                coreDataId: group.scores[0]?.coreDataId,
                studyType: group.scores[0]?.studyType,
                studyTypeMethod: group.scores[0]?.studyTypeMethod,
                isCompetitorProduct: group.scores[0]?.isCompetitorProduct,
                scores: group.scores,
                sampleSize: group.scores[0]?.sampleSize,
                products: productsArr,
            };
        });

        const sortedData = filteredRow.sort((a, b) => {
            const getCompNumber = (str) => parseInt(str.split("_").pop());
            return getCompNumber(a?.backendVariable) - getCompNumber(b?.backendVariable);
        });
        setProductsDataForTTest(sortedData);
    }, [productsSecondaryDataState]);

    const handleSelectedMicroCategory = (microCategory) => {
        dispatch(loadSelectedMicroCategoryState(microCategory));
    };

    const clearResult = useCallback(() => {
        setTTestResult([]);
        setTestLabel("");
        dispatch(resetTTestStore());
    }, [dispatch]);

    const clearTtest = useCallback(
        (product, type) => {
            // clear t test
            if (type === "consumerFilterAll") {
                clearResult();
            } else if (type === "consumerFilter") {
                const tTestPro = tTestResult?.[0]?.colors?.map((datum) => datum.productCode) || [];
                const cfPc = product?.split("_")?.join(" ") || "";
                if (tTestPro.indexOf(cfPc) !== -1) {
                    clearResult();
                }
            }
        },
        [clearResult, tTestResult]
    );

    const getKpisFromResponse = (response) => {
        const kpis = new Set(
            response
                .map((datum) => {
                    const _kpi = datum?.action_standard_kpis?.map((item) => item?.kpi || []);
                    return _kpi.flat();
                })
                .flat()
        );

        return [...kpis];
    };

    const getActionStandard = useCallback(() => {
        if (selectedStudy.length === 1) {
            const coreDataId = selectedStudy[0]?.id || null;
            if (coreDataId) {
                const url = process.env.REACT_APP_SERVER_PATH + `/action-standard-groups/submit/kpis/?archive=False&core_data_model=${coreDataId}`;
                try {
                    api.get(url)
                        .then(async (res) => {
                            const response = res.data;
                            const kpis = getKpisFromResponse(response);
                            const kpisArray = kpis.map(item => item.backend_variable)
                            setActionStandardKpis([...kpisArray]);
                            setActionStandardData(response);
                        })
                        .catch(async (error) => {
                            handleError("error", error);
                        });
                } catch (error) {
                    handleError("error", error);
                }
            }
        }
    }, [selectedStudy]);

    useEffect(() => {
        getActionStandard();
    }, [getActionStandard]);

    const getPreferenceResult = useCallback(
        async ({ product = null, coreDataId = null, type = null, consumerFilter = null, pcs = [], selectedStudy = [], allAnalysisData = {}, fetchOnlyCurrent = false }) => {
            clearTtest(product, type);

            dispatch(loadIsProductPreferenceAnalysisResultLoadingState(true));

            const productPreferenceResult = await GetProductPreferenceResult({
                product,
                coreDataId,
                type,
                consumerFilter,
                pcs,
                selectedStudy,
                productsSecondaryDataState: allAnalysisData?.productPreferenceAnalysisResult || [],
                productsOrder,
                productCategoryId,
            });
            if (productPreferenceResult) {
                await dispatch(loadProductPreferenceAnalysisResultState([...productPreferenceResult]));
                if (!type) {
                    dispatch(loadDefaultProductPreferenceAnalysisResultState([...productPreferenceResult]));
                }
            }
            dispatch(loadIsProductPreferenceAnalysisResultLoadingState(false));
            console.log("fetchOnlyCurrent", fetchOnlyCurrent);
        },
        [dispatch, productCategoryId, productsOrder, clearTtest]
    );

    // Handle error function
    const handleError = useCallback(
        (type, error) => {
            dispatch(showNotification({ severity: type, data: error }));
        },
        [dispatch]
    );

    useEffect(() => {
        async function fetchDataMappingData() {
            try {
                const selectedStudyTypeIds = [...new Set(selectedStudy.map((datum) => datum.study_type))].join(",");
                const selectedStudyTypeMethodIds = [...new Set(selectedStudy.map((datum) => datum.study_type_method))].join(",");
                let url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/all/?archive=false&uniq=true&variable_category=${encodeURIComponent("Product Preference Analysis")}`;
                if (productCategoryId) {
                    url += `&product_category=${productCategoryId}&study_type=${selectedStudyTypeIds}&study_type_method=${selectedStudyTypeMethodIds}`;
                }
                setIsDataMappingDataLoading(true);
                await api
                    .get(url)
                    .then(async (res) => {
                        setDataMappingData(res.data);
                        setIsDataMappingDataLoading(false);
                    })
                    .catch((error) => {
                        handleError("error", error);
                        setIsDataMappingDataLoading(false);
                    });
            } catch (error) {
                handleError("error", error);
                setIsDataMappingDataLoading(false);
            }
        }
        const fetchAnalysisResult = async () => {
            if (!productsSecondaryDataState.length) {
                await getPreferenceResult({
                    product: null,
                    coreDataId: null,
                    type: null,
                    consumerFilter: null,
                    pcs: [],
                    selectedStudy,
                    allAnalysisData: [],
                    fetchOnlyCurrent: true,
                });
            }
        };

        if (selectedStudy.length === 0) {
            navigate("/studies");
        } else {
            fetchAnalysisResult();
            fetchDataMappingData();
        }
    }, [selectedStudy, productsSecondaryDataState, productCategoryId, getPreferenceResult, handleError, navigate]);

    // Clear the removed KPI data from the Redux store on page change to reset the default view.
    // Remove the code below if you want to retain the removed variables.
    useEffect(() => {
        return () => {
            dispatch(loadPreferenceRemovedKpiListState([]));
        };
    }, [dispatch]);

    useEffect(() => {
        clearResult();
        if (percentageToggle) {
            dispatch(loadProductPreferenceAnalysisTypeValueState({ value: "percentage", label: "Percentage" }));
        } else {
            dispatch(loadProductPreferenceAnalysisTypeValueState({ value: "count", label: "Count" }));
        }
    }, [percentageToggle]);

    const handlePercentageToggleChange = () => {
        dispatch(loadPercentageToggleState(!percentageToggle));
    };

    const activeTabMappings = dataMappingData?.columns?.find((ele) => ele.macroCategory === activeTab)?.displayLabels || [];
    const activeBackendVariables = activeTabMappings.map((mapping) => mapping.backendVariable);
    const showActionStandards = actionStandardKpis.some((kpis) => activeBackendVariables.includes(kpis)) && selectedStudy.length === 1;

    return (
        <>
            {(isLoading || isDataMappingDataLoading) && <Loader />}
            <Header
                title={headerTitle}
                isStudySelected={true}
            />
            <div
                className="ms-4 me-4 mb-0 mt-3 background-color-white box-shadow--2 d-flex"
                style={{
                    borderRadius: "5px",
                }}
            >
                <FiltersHeaderLabel multipleLabel={true} />
            </div>
            <FullScreenConatiner>
                <div
                    className=""
                    style={{
                        width: 150,
                        display: "flex",
                        alignItems: "center",
                        position: "absolute",
                        zIndex: 1,
                    }}
                >
                    <Link
                        to="/studies"
                        onClick={(e) => {
                            if (window.history.length > 2) {
                                e.preventDefault();
                                navigate(-1);
                            }
                        }}
                    >
                        <div
                            className="p-2 liner-light-blue-background color-white"
                            style={{
                                borderRadius: "50%",
                                position: "relative",
                                top: -15,
                                left: -15,
                                cursor: "pointer",
                            }}
                        >
                            <span>
                                <ArrowBackIcon className="color-white" />
                            </span>
                        </div>
                    </Link>
                </div>

                <div
                    className="card-btns"
                    style={{ padding: "20px 20px 20px 20px" }}
                >
                    <div
                        style={{
                            display: "flex",
                            zIndex: 9,
                        }}
                    >
                        <div>
                            <Button
                                size="small"
                                className="custom-btn custom-btn-outlined-2 color-blue bold-content ms-2 pb-1"
                                variant="outlined"
                            >
                                Percentage
                                <Switch
                                    size="small"
                                    checked={percentageToggle}
                                    onChange={handlePercentageToggleChange}
                                />
                            </Button>
                        </div>
                    </div>
                    <div
                        style={{
                            display: "flex",
                            zIndex: 9,
                            marginLeft: "320px",
                        }}
                    >
                        <div
                            className="color-blue text-bold-7"
                            style={{
                                marginBottom: "5px",
                                marginRight: "5px",
                                marginTop: "10px",
                            }}
                        >
                            {`Overall ${activeTab} Preference ${showActionStandards ? "*" : ""}`}
                        </div>
                    </div>

                    <div>
                        <FilterAcrossProductsToggleComponent />
                        <TTestDialog
                            variableCategory="Product Preference Analysis"
                            functionsSettings={{
                                setIsFisher: setIsFisher,
                                setLegends: setLegends,
                                setTTestResult: setTTestResult,
                                setTestLabel: setTestLabel,
                                clearResult: clearResult,
                            }}
                            analysisLevelData={{
                                moduleName: "productPreference",
                                analysisTypeValue: analysisTypeValue,
                                boxAnalysisTypeSelection: [],
                                boxAnalysisSelection: {},
                            }}
                            tabData={{
                                productsSecondaryDataState: productsDataForTTest,
                            }}
                        />
                        <Reset clearTTestResult={clearResult} />
                    </div>
                </div>
                <div
                    style={{
                        minHeight: "calc(100vh - 300px)",
                        padding: "20px",
                    }}
                >
                    <PageLayout
                        mappings={dataMappingData?.columns?.find((ele) => ele.macroCategory === activeTab)}
                        handleFilterSubmit={getPreferenceResult}
                        tTest={{
                            tTestLegends: legends,
                            isFisher: isFisher,
                            tTestResult: tTestResult,
                            testLabel: testLabel,
                        }}
                        moduleName="productPreference"
                    />
                    {!isLoading && !isDataMappingDataLoading && productsSecondaryDataState.length !== 0 && (
                        <SkewTabs
                            tableData={{
                                productsSecondaryDataState: productsSecondaryDataState,
                            }}
                            settings={{
                                activeTab: activeTab,
                                setActiveTab: handleSelectedMicroCategory,
                            }}
                            analysisView={{
                                moduleName: "productPreference",
                                mappings: dataMappingData.columns,
                                analysisTypeValue: analysisTypeValue,
                                boxAnalysisTypeSelection: [],
                            }}
                            actionStandard={{ showActionStandards: showActionStandards }}
                        />
                    )}
                    <AnalysisTableBottomLabel
                        actionStandard={{
                            actionStandardKpis: actionStandardKpis,
                            actionStandardData: actionStandardData,
                            showActionStandardsInfo: true,
                        }}
                        analysisView={{ moduleName: "productPreference" }}
                    />
                </div>
                <div
                    style={{
                        position: "relative",
                        top: "5px",
                        marginTop: "30px",
                    }}
                >
                    <MainNavigation
                        buttons={btnArr}
                        isFragrance={true}
                        btnDisabledObj={btnDisabled}
                    />
                </div>
            </FullScreenConatiner>
        </>
    );
}

// Typechecking props for the PreferenceAnalysisExpressCLT
PreferenceAnalysisExpressCLT.propTypes = {
    headerTitle: PropTypes.string,
};

export default PreferenceAnalysisExpressCLT;
